import React, {ReactElement} from "react"
import {useAccessToken} from "../../services/auth"
import {usePaging} from "../../hooks/usePaging"
import {AsyncResultComponent} from "../../containers/AsyncResult/AsyncResult"
import {findApplicationForms, ApplicationFormListItem} from "../../resources/applicationForm"
import moment from "moment"
import PagingNavBar from "../../components/PagingNavBar/PagingNavBar"
import {
	DataTableRow,
	DataTableCell,
	DataTableHead,
	DataTableBody,
	DataTableActionHeader,
	DataTable,
	DataTableCard,
	TablePending,
} from "../DataTable/DataTable"
import classNames from "classnames"
import {useNavigate} from "react-router-dom"
import IdLink from "../IdLink/IdLink"

export enum ApplicationFormsColumns {
	id = "Id",
	url = "URL",
	org = "Org",
	createdAt = "Created At",
	updatedAt = "Updated At",
}

type AllowedApplicationFormsColumns =
	| ApplicationFormsColumns.id
	| ApplicationFormsColumns.url
	| ApplicationFormsColumns.org
	| ApplicationFormsColumns.createdAt
	| ApplicationFormsColumns.updatedAt

function ApplicationFormRow({
	applicationForm,
	includedColumns,
}: {
	applicationForm: ApplicationFormListItem
	includedColumns: Array<AllowedApplicationFormsColumns>
}) {
	const navigate = useNavigate()
	const contentColumns: Partial<Record<AllowedApplicationFormsColumns, ReactElement>> = {}

	if (includedColumns.includes(ApplicationFormsColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className={classNames("data-table-id-cell")}>
				<IdLink className="data-table-id" href={`/application-forms/${applicationForm.id}`}>
					{applicationForm.id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(ApplicationFormsColumns.url)) {
		contentColumns["URL"] = (
			<DataTableCell>
				<a href={applicationForm.attributes.url} target="_blank" rel="noopener noreferrer">
					{applicationForm.attributes.url}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(ApplicationFormsColumns.org)) {
		contentColumns["Org"] = <DataTableCell>{applicationForm.relationships?.org?.data?.id || "-"}</DataTableCell>
	}

	if (includedColumns.includes(ApplicationFormsColumns.createdAt)) {
		contentColumns["Created At"] = (
			<DataTableCell>{moment(applicationForm.attributes.createdAt).format("MMM DD, YYYY HH:mm")}</DataTableCell>
		)
	}

	if (includedColumns.includes(ApplicationFormsColumns.updatedAt)) {
		contentColumns["Updated At"] = (
			<DataTableCell>{moment(applicationForm.attributes.updatedAt).format("MMM DD, YYYY HH:mm")}</DataTableCell>
		)
	}

	const content = includedColumns.map((col) => contentColumns[col]).filter(Boolean)

	return <DataTableRow onClick={() => navigate(`/application-forms/${applicationForm.id}`)}>{content}</DataTableRow>
}

export function ApplicationForms({
	fullHeight = true,
	enableTitle = false,
	includedColumns,
	refreshToken,
}: {
	fullHeight?: boolean
	enableTitle?: boolean
	includedColumns: Array<AllowedApplicationFormsColumns>
	refreshToken?: number
}) {
	const accessToken = useAccessToken()
	const prefix = "af-"

	const [result, hasPrev, hasNext, prev, next, hasResults] = usePaging(
		25,
		(offset, limit) => findApplicationForms(accessToken, offset, limit),
		(x) => x.data.length,
		[refreshToken],
		prefix,
		true
	)

	return (
		<DataTableCard className={"application-forms-card"}>
			<DataTableActionHeader enableSticky={fullHeight} title={enableTitle ? "Application Forms" : null} />
			<AsyncResultComponent
				asyncResult={result}
				pendingComponent={<TablePending numberOfRows={includedColumns.length} />}
			>
				{({value: applicationForms}) => {
					const rows = applicationForms.data.map((af) => (
						<ApplicationFormRow key={af.id} applicationForm={af} includedColumns={includedColumns} />
					))

					return (
						<div className={"application-forms-table"}>
							<DataTable>
								<DataTableHead>
									<DataTableRow>
										{Object.entries(includedColumns).map((column) => {
											return <DataTableCell key={column[0]}>{column[1]}</DataTableCell>
										})}
									</DataTableRow>
								</DataTableHead>
								<DataTableBody>{rows}</DataTableBody>
							</DataTable>
							<PagingNavBar hasResults={hasResults} hasPrev={hasPrev} hasNext={hasNext} prev={prev} next={next} />
						</div>
					)
				}}
			</AsyncResultComponent>
		</DataTableCard>
	)
}
